import React, { useEffect, useState } from "react";
import {
  CashInformation,
  IContributionTransaction,
  OtherAssetsInformation,
  SecuritiesInformation,
} from "interfaces/contribution-transaction";
import { Show } from "@refinedev/mui";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useShow } from "@refinedev/core";
import { primary } from "style/color";
import { EvaluateContributionTransactionModal } from "components/modal/evaluate-contribution-transaction";
import { useParams } from "react-router-dom";
import { ProcessContributionTransactionModal } from "components/modal/process-contribution-transaction";
import { moneyFormat } from "utils/money-format";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import { shortFormat } from "utils/date-format";
import SelectSageCustomerModal from "components/modal/select-sage-customer-modal";
import { axiosInstance } from "providers/rest-data-provider/utils";
import awsExports from "aws-exports";

const API_BASE_URL = awsExports.endpoints[0].endpoint;

interface RevenueAccount {
  accountId: string;
  accountName: string;
}

interface JournalEntryDialogState {
  open: boolean;
  title: string;
  message: string;
}

const CLOSED_JOURNAL_ENTRY_DIALOG: JournalEntryDialogState = {
  open: false,
  title: "",
  message: "",
};

export const ContributionTransactionDetails: React.FC = () => {
  const { id } = useParams();
  const { queryResult } = useShow<IContributionTransaction>({
    resource: "contribution-transactions",
    id: id,
  });
  const [open, setOpen] = useState(false);
  const [openProcess, setOpenProcess] = useState(false);
  const [openSelectCustomer, setOpenSelectCustomer] = useState(false);
  const [decision, setDecision] = useState<{
    contributionTransaction: IContributionTransaction | undefined;
    value: "PENDING" | "ACCEPTED" | "REJECTED";
  }>({ contributionTransaction: undefined, value: "PENDING" });
  const [revenueAccounts, setRevenueAccounts] = useState<RevenueAccount[]>([]);
  const [selectedRevenueAccount, setSelectedRevenueAccount] = useState("");
  const [isPreparingJournalEntry, setIsPreparingJournalEntry] =
    useState(false);
  const [isSyncingJournalEntry, setIsSyncingJournalEntry] = useState(false);
  const [openPaymentSourceDialog, setOpenPaymentSourceDialog] =
    useState(false);
  const [journalEntryDialog, setJournalEntryDialog] =
    useState<JournalEntryDialogState>(CLOSED_JOURNAL_ENTRY_DIALOG);
  const { data, refetch } = queryResult;
  const record = data?.data;

  useEffect(() => {
    axiosInstance
      .get(`${API_BASE_URL}/accounting-settings`)
      .then((response) => {
        setRevenueAccounts(response.data?.availableRevenueAccounts ?? []);
      })
      .catch(() => {
        setRevenueAccounts([]);
      });
  }, []);

  const handleOpenCreateJournalEntry = async () => {
    if (!record) return;
    setIsPreparingJournalEntry(true);
    try {
      let defaultRevenueAccount = "";
      if (record.sageIntacctCustomerId) {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/sage-intacct/customers/${record.sageIntacctCustomerId}`
        );
        if (response.data?.success && response.data?.customer?.ARACCOUNT) {
          defaultRevenueAccount = response.data.customer.ARACCOUNT;
        }
      }
      setSelectedRevenueAccount(defaultRevenueAccount);
      setOpenPaymentSourceDialog(true);
    } catch (error: any) {
      setSelectedRevenueAccount("");
      setOpenPaymentSourceDialog(true);
    } finally {
      setIsPreparingJournalEntry(false);
    }
  };

  const handleCreateJournalEntry = async (
    paymentSource: "investment" | "bank"
  ) => {
    if (!record || !selectedRevenueAccount) return;
    setOpenPaymentSourceDialog(false);
    setIsSyncingJournalEntry(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/contribution-transactions/${record.contributionId}/sync-journal-entry`,
        { paymentSource, revenueAccount: selectedRevenueAccount }
      );
      const result = response.data;

      if (!result.success) {
        setJournalEntryDialog({
          open: true,
          title: "Create Journal Entry Failed",
          message: `Create Journal Entry request failed: ${result.message}`,
        });
        return;
      }

      await refetch();
      setJournalEntryDialog({
        open: true,
        title: "Create Journal Entry",
        message:
          "Journal entry has been successfully created for this contribution.",
      });
    } catch (error: any) {
      setJournalEntryDialog({
        open: true,
        title: "Create Journal Entry Failed",
        message: `Create Journal Entry request failed: ${
          error?.response?.data?.message ??
          error?.message ??
          "An unexpected error occurred."
        }`,
      });
    } finally {
      setIsSyncingJournalEntry(false);
    }
  };
  return (
    <Show
      isLoading={queryResult.isLoading}
      title={
        <Typography variant="h5" color={primary}>
          Contribution Transaction Details
        </Typography>
      }
    >
      {queryResult?.status === "loading" ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          {data?.data.status === "PROCESSED" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              {record?.status === "PROCESSED" && (
                <>
                  <SelectSageCustomerModal
                    open={openSelectCustomer}
                    onClose={() => setOpenSelectCustomer(false)}
                    contribution={record}
                    onSaved={() => refetch()}
                  />
                  <Button
                    size="large"
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenSelectCustomer(true)}
                  >
                    {record.sageIntacctCustomerId
                      ? "Change Customer"
                      : "Select Customer"}
                  </Button>
                </>
              )}
              {record?.status === "PROCESSED" &&
                record.sageIntacctCustomerId &&
                !record.sageIntacctTransactionId && (
                  <Button
                    size="large"
                    variant="outlined"
                    color="primary"
                    disabled={isPreparingJournalEntry || isSyncingJournalEntry}
                    startIcon={
                      isPreparingJournalEntry || isSyncingJournalEntry ? (
                        <CircularProgress size={16} />
                      ) : undefined
                    }
                    onClick={handleOpenCreateJournalEntry}
                  >
                    Create Journal Entry
                  </Button>
                )}
              {data?.data.receipt?.url && (
                <Link
                  href={data?.data.receipt?.url}
                  target="__blank"
                  underline="none"
                  color="inherit"
                >
                  <Button startIcon={<ArrowCircleDownIcon />} variant="contained">
                    Download
                  </Button>
                </Link>
              )}
            </Box>
          )}
          <TextField
            value={`${record?.user?.title ? record?.user?.title : ""} ${
              record?.user?.firstName ? record?.user?.firstName : ""
            } ${record?.user?.middleName ? record?.user?.middleName : ""} ${
              record?.user?.lastName ? record.user?.lastName : ""
            } ${record?.user?.suffix ? record?.user?.suffix : ""}`}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="User "
            disabled
          />
          <TextField
            value={`${
              record?.contributionReport?.name
                ? record?.contributionReport?.name
                : ""
            }`}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Donor "
            disabled
          />
          <TextField
            value={`${
              record?.contributionReport?.donorEmail
                ? record?.contributionReport?.donorEmail
                : ""
            }`}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Donor email"
            disabled
          />
          <TextField
            value={`${record?.contributionReport?.addressLine1} ${record?.contributionReport?.addressLine2}, ${record?.contributionReport?.city} ${record?.contributionReport?.state} ${record?.contributionReport?.zip}`}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Donor address"
            disabled
          />
          <TextField
            disabled
            value={record?.fund.fundAccountName}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Fund Name"
          />
          <TextField
            disabled
            value={shortFormat(record?.createdAt)}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Creation Date"
          />
          {record?.status === "REJECTED" && (
            <TextField
              disabled
              value={shortFormat(record?.updatedAt)}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Rejection Date"
            />
          )}
          {record?.status === "CANCELED" && (
            <TextField
              disabled
              value={shortFormat(record?.updatedAt)}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Cancellation Date"
            />
          )}
          {record?.status === "PROCESSED" && (
            <TextField
              disabled
              value={shortFormat(record?.executionDate)}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Process Date"
            />
          )}
          {record?.status === "ACCEPTED" && (
            <TextField
              disabled
              value={shortFormat(record?.executionDate)}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Process Date"
            />
          )}
          <TextField
            disabled
            value={record?.status}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Status "
          />
          <TextField
            disabled
            value={record?.type}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Contribution Transaction Type "
          />
          {record?.type === "CASH" && (
            <>
              <TextField
                disabled
                //@ts-ignore
                value={moneyFormat(
                  (record?.contributionInformation as CashInformation)
                    .requestedAmount
                )}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Requested Amount"
              />
              {record?.contributionReport.appliedAmount &&
                record.status === "PROCESSED" && (
                  <TextField
                    disabled
                    value={moneyFormat(
                      //@ts-ignore
                      (record?.contributionReport as CashInformation)
                        .appliedAmount
                    )}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Applied amount"
                  />
                )}
              <TextField
                disabled
                //@ts-ignore
                value={record?.contributionInformation?.paymentMethod}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Payment method"
              />
            </>
          )}
          {record?.type === "SECURITIES" && (
            <>
              <TextField
                disabled
                //@ts-ignore
                value={record?.contributionInformation?.securityName}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Security Name"
              />
              <TextField
                disabled
                //@ts-ignore
                value={record?.contributionInformation?.ticker}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Ticker"
              />
              <TextField
                disabled
                //@ts-ignore
                value={record?.contributionInformation?.quantity}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Quantity"
              />

              {record?.contributionInformation &&
                (record?.contributionInformation as SecuritiesInformation)
                  .estimatedMarketRate && (
                  <TextField
                    disabled
                    //@ts-ignore
                    value={moneyFormat(
                      //@ts-ignore
                      record?.contributionInformation.estimatedMarketRate
                    )}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Estimated Market Rate"
                  />
                )}
              {record?.contributionInformation &&
                (record?.contributionInformation as SecuritiesInformation)
                  .estimatedAmount && (
                  <TextField
                    disabled
                    value={moneyFormat(
                      //@ts-ignore
                      record?.contributionInformation.estimatedAmount
                    )}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Estimated Amount"
                  />
                )}

              {record?.contributionReport &&
                record?.contributionReport.marketRate &&
                record.status === "PROCESSED" && (
                  <TextField
                    disabled
                    //@ts-ignore
                    value={record?.contributionReport.marketRate}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Market Rate"
                  />
                )}

              {record?.contributionReport &&
                record?.contributionReport.appliedAmount &&
                record.status === "PROCESSED" && (
                  <TextField
                    disabled
                    //@ts-ignore
                    value={moneyFormat(
                      //@ts-ignore
                      record?.contributionReport.appliedAmount
                    )}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Applied Amount"
                  />
                )}
            </>
          )}
          {record?.type === "OTHER" && (
            <>
              <TextField
                disabled
                //@ts-ignore
                value={record?.contributionInformation?.description}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Description"
              />
              {(record?.contributionInformation as OtherAssetsInformation)
                .estimatedAmount && (
                <TextField
                  disabled
                  //@ts-ignore
                  value={record?.contributionInformation?.estimatedAmount}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label="Estimated amount"
                />
              )}
              {record?.contributionReport.appliedAmount &&
                record.status === "PROCESSED" && (
                  <TextField
                    disabled
                    value={moneyFormat(
                      //@ts-ignore
                      record?.contributionReport.appliedAmount
                    )}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Applied amount"
                  />
                )}
            </>
          )}
          {record?.status === "REJECTED" && record.rejectionReason && (
            <TextField
              disabled
              value={record.rejectionReason}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Rejection reason "
            />
          )}
          {record?.emailToNotify && (
            <TextField
              disabled
              value={record.emailToNotify}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Email to notify"
              type="text"
            />
          )}
          {record?.reference && (
            <TextField
              disabled
              value={record.reference}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="textarea"
              label="Reference "
              minRows={3}
              rows={2}
              maxRows={4}
              multiline
            />
          )}
          {record?.status === "PROCESSED" && record.sageIntacctCustomerId && (
            <TextField
              disabled
              value={record.sageIntacctCustomerId}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Sage Intacct Customer ID"
            />
          )}
          {record?.status === "PROCESSED" && record.sageIntacctTransactionId && (
            <TextField
              disabled
              value={record.sageIntacctTransactionId}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Sage Intacct Transaction ID"
            />
          )}
          {record?.extraNotes && (
            <TextField
              disabled
              value={record.extraNotes}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Note"
              type="textarea"
              minRows={5}
              rows={2}
              maxRows={4}
              multiline
            />
          )}
          {record?.status === "PENDING" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",

                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <EvaluateContributionTransactionModal
                visible={open}
                close={setOpen}
                data={decision}
              />

              <Button
                size="large"
                variant="contained"
                style={{ marginRight: "0.5rem" }}
                color="primary"
                onClick={() => {
                  setOpen(true);
                  setDecision({
                    contributionTransaction: record,
                    value: "ACCEPTED",
                  });
                }}
              >
                Accept
              </Button>
              <Button
                size="large"
                variant="outlined"
                color="primary"
                onClick={() => {
                  setOpen(true);
                  setDecision({
                    contributionTransaction: record,
                    value: "REJECTED",
                  });
                }}
              >
                Reject
              </Button>
            </Box>
          )}
          {record?.status === "ACCEPTED" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",

                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <ProcessContributionTransactionModal
                visible={openProcess}
                close={setOpenProcess}
                contributionTransaction={record}
              />

              <Button
                size="large"
                variant="contained"
                style={{ marginRight: "0.5rem" }}
                color="primary"
                onClick={() => {
                  setOpenProcess(true);
                  setDecision({
                    contributionTransaction: record,
                    value: "ACCEPTED",
                  });
                }}
              >
                Process
              </Button>
            </Box>
          )}
          <Dialog
            open={openPaymentSourceDialog}
            onClose={() => setOpenPaymentSourceDialog(false)}
          >
            <DialogTitle>Create Journal Entry</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Which account should this contribution be posted to?
              </DialogContentText>
              <TextField
                select
                fullWidth
                label="Revenue Account"
                value={selectedRevenueAccount}
                onChange={(e) => setSelectedRevenueAccount(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {revenueAccounts.map((account) => (
                  <MenuItem key={account.accountId} value={account.accountId}>
                    {account.accountId} - {account.accountName}
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenPaymentSourceDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                disabled={!selectedRevenueAccount}
                onClick={() => handleCreateJournalEntry("investment")}
              >
                Pay Directly from Investment Account
              </Button>
              <Button
                variant="contained"
                disabled={!selectedRevenueAccount}
                onClick={() => handleCreateJournalEntry("bank")}
              >
                Route through Bank Account
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={journalEntryDialog.open}
            onClose={() => setJournalEntryDialog(CLOSED_JOURNAL_ENTRY_DIALOG)}
          >
            <DialogTitle>{journalEntryDialog.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{journalEntryDialog.message}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setJournalEntryDialog(CLOSED_JOURNAL_ENTRY_DIALOG)
                }
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Show>
  );
};
